<template>
  <div class="card p-3 mt-4">
    <h5 class="mb-3 section-title card-section-title">Totales Terminados por Producto</h5>
    <!-- Filtros: buscador izquierda, fechas derecha (mismo tamaño y posición en todas las tarjetas) -->
    <div class="card-filters-row d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
      <div class="card-filters-buscar">
        <label for="buscar_terminados" class="form-label">Buscar</label>
        <input
          id="buscar_terminados"
          type="text"
          class="form-control"
          v-model="buscar"
          placeholder="Buscar por producto..."
        />
      </div>
      <div class="card-filters-fechas d-flex flex-wrap align-items-end gap-5">
        <div class="card-filters-fecha">
          <label for="fecha_inicio" class="form-label">Fecha inicio</label>
          <input id="fecha_inicio" type="date" class="form-control" v-model="fecha_inicio" />
        </div>
        <div class="card-filters-fecha">
          <label for="fecha_fin" class="form-label">Fecha fin</label>
          <input id="fecha_fin" type="date" class="form-control" v-model="fecha_fin" />
        </div>
      </div>
    </div>
    <div class="row">
      <!-- Detalle de producción (izquierda) -->
      <div class="col-lg-6">
        <div class="table-responsive">
          <table class="table table-sm table-totales-terminados">
            <thead>
              <tr>
                <th>ID</th>
                <th class="text-start">Producto</th>
                <th>Primera</th>
                <th>Segunda</th>
                <th>Tercera</th>
                <th>Total</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in tableRows" :key="item ? item.id : `empty-${idx}`" :class="{ 'fila-vacia': !item }">
                <td>{{ item ? item.id : '—' }}</td>
                <td class="text-start">
                  {{ item ? (item.inventory?.name || item.inventory_id) : '—' }}
                </td>
                <td>{{ item ? item.primera : '—' }}</td>
                <td>{{ item ? item.segunda : '—' }}</td>
                <td>{{ item ? item.tercera : '—' }}</td>
                <td>{{ item ? (item.primera + item.segunda + item.tercera) : '—' }}</td>
                <td>{{ item ? formatDate(item.created_at) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div class="totales-pagination d-flex justify-content-center align-items-center mt-2 gap-2">
          <button
            type="button"
            class="totales-pagination-btn"
            @click="prevPage"
            :disabled="pageDisplay <= 1"
          >
            &lt;&lt;
          </button>
          <span class="totales-pagination-text">{{ pageDisplay }}/{{ lastPageDisplay }}</span>
          <button
            type="button"
            class="totales-pagination-btn"
            @click="nextPage"
            :disabled="pageDisplay >= lastPageDisplay"
          >
            &gt;&gt;
          </button>
        </div>
      </div>

      <!-- Gráfico y totales (derecha) -->
      <div class="col-lg-6">
        <div class="totales-chart-wrap">
          <canvas ref="totalesChart"></canvas>
        </div>
        <ul class="mt-3 totales-resumen list-unstyled text-center">
          <li><strong>Total:</strong> {{ totalGeneralDisplay }} unidades</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default {
  name: "TotalesTerminados",
  data() {
    const hoy = new Date();
    const hace15 = new Date();
    hace15.setDate(hoy.getDate() - 15);

    const formatDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    return {
      fecha_inicio: formatDate(hace15),
      fecha_fin: formatDate(hoy),
      buscar: '',
      totales: {
        total_primera: 0,
        total_segunda: 0,
        total_tercera: 0,
        total_general: 0,
      },
      listado: { data: [], current_page: 1, last_page: 1 },
      chartInstance: null,
      debounceTimer: null,
      fetchSeq: 0,
      chartRetryTimer: null,
    };
  },
  computed: {
    tableRows() {
      const minRows = 6;
      const data = this.listado.data || [];
      const rows = [...data];
      while (rows.length < minRows) rows.push(null);
      return rows;
    },
    pageDisplay() {
      const l = this.listado;
      if (!l) return 1;
      const last = Math.max(1, l.last_page ?? 1);
      const cur = Math.min(Math.max(1, l.current_page ?? 1), last);
      return cur;
    },
    lastPageDisplay() {
      return Math.max(1, this.listado?.last_page ?? 1);
    },
    totalGeneralDisplay() {
      return Number(this.totales?.total_general) || 0;
    },
    // Totales para el gráfico: usar SIEMPRE los totales del backend (suma global Primera, Segunda, Tercera)
    chartTotals() {
      return {
        total_primera: Number(this.totales?.total_primera) || 0,
        total_segunda: Number(this.totales?.total_segunda) || 0,
        total_tercera: Number(this.totales?.total_tercera) || 0,
      };
    },
  },
  watch: {
    fecha_inicio() {
      this.fetchTotales(1);
    },
    fecha_fin() {
      this.fetchTotales(1);
    },
    buscar() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchTotales(1), 400);
    },
    totales: {
      handler() {
        this.$nextTick(() => this.renderChart(0));
      },
      deep: true,
    },
  },
  methods: {
    async fetchTotales(page = 1) {
      try {
        const reqId = ++this.fetchSeq;
        const params = {
          fecha_inicio: this.fecha_inicio,
          fecha_fin: this.fecha_fin,
          page,
        };
        if (this.buscar && this.buscar.trim() !== '') {
          params.buscar = this.buscar.trim();
        }
        const res = await this.$store.dispatch("analitycs/getTerminados", params);
        const data = res?.data?.data ?? res?.data ?? {};
        if (reqId !== this.fetchSeq) return; // evita pisar estado con respuestas viejas

        this.listado = data.listado || { data: [] };
        this.normalizeListadoPagination();
        this.totales = this.normalizeTotales(data.totales);
        this.$nextTick(() => this.renderChart(0));
      } catch (error) {
        console.error("Error cargando totales terminados:", error);
      }
    },

    renderChart(retry = 0) {
      const canvas = this.$refs.totalesChart;
      if (!canvas || typeof canvas.getContext !== "function") {
        if (retry < 5) {
          setTimeout(() => this.renderChart(retry + 1), 120);
        }
        return;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const existingChart = Chart.getChart(canvas);
      if (existingChart) existingChart.destroy();
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
      // Si el canvas aun no tiene tamano visible (montaje/layout), reintentar.
      const width = canvas.clientWidth || canvas.parentElement?.clientWidth || 0;
      const height = canvas.clientHeight || canvas.parentElement?.clientHeight || 0;
      if ((width === 0 || height === 0) && retry < 10) {
        if (this.chartRetryTimer) clearTimeout(this.chartRetryTimer);
        this.chartRetryTimer = setTimeout(() => this.renderChart(retry + 1), 120);
        return;
      }
      const labels = ["Primera", "Segunda", "Tercera"];
      const t = this.chartTotals;
      const totalPrimera = Number(t.total_primera) || 0;
      const totalSegunda = Number(t.total_segunda) || 0;
      const totalTercera = Number(t.total_tercera) || 0;
      const dataTotales = [totalPrimera, totalSegunda, totalTercera];
      try {
        this.chartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Unidades",
              data: dataTotales,
              backgroundColor: ["#36A2EB", "#FFCE56", "#FF6384"],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "nearest",
            intersect: false,
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              callbacks: {
                label(context) {
                  const value = Number(context.raw) || 0;
                  return `${context.label}: ${value}`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: false,
              },
            },
            y: {
              beginAtZero: true,
            },
          },
        },
      });
      } catch (err) {
        console.error("[TotalesTerminados] Error al dibujar gráfico:", err);
      }
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleString("es-ES");
    },

    normalizeListadoPagination() {
      const l = this.listado;
      if (!l || typeof l !== 'object') return;
      const last = Math.max(1, parseInt(l.last_page, 10) || 1);
      let cur = parseInt(l.current_page, 10) || 1;
      cur = Math.min(Math.max(1, cur), last);
      l.current_page = cur;
      l.last_page = last;
      if (!Array.isArray(l.data)) l.data = [];
    },
    normalizeTotales(rawTotales) {
      // Backend devuelve un solo registro con SUM(primera), SUM(segunda), etc.
      const row = Array.isArray(rawTotales) ? (rawTotales[0] || {}) : (rawTotales || {});
      const total_primera = Number(row.total_primera) || 0;
      const total_segunda = Number(row.total_segunda) || 0;
      const total_tercera = Number(row.total_tercera) || 0;
      const total_general = Number(row.total_general) || (total_primera + total_segunda + total_tercera);
      return {
        total_primera,
        total_segunda,
        total_tercera,
        total_general,
      };
    },

    nextPage() {
      if (this.pageDisplay < this.lastPageDisplay) {
        this.fetchTotales(this.pageDisplay + 1);
      }
    },
    prevPage() {
      if (this.pageDisplay > 1) {
        this.fetchTotales(this.pageDisplay - 1);
      }
    },
    handleResize() {
      this.$nextTick(() => this.renderChart(0));
    },
  },
  mounted() {
    this.fetchTotales();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    if (this.chartRetryTimer) {
      clearTimeout(this.chartRetryTimer);
      this.chartRetryTimer = null;
    }
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  },
};
</script>

<style scoped>
.card-section-title.section-title {
  font-weight: 800;
  color: #1f2d5a;
  letter-spacing: 0.6px;
}

.card-section-title.section-title::after {
  content: "";
  display: block;
  width: 44px;
  height: 3px;
  margin-top: 6px;
  border-radius: 999px;
  background: #f6a500;
}

.table.table-totales-terminados {
  font-size: 0.8rem;
}

/* Líneas de la tabla muy suaves */
.table-totales-terminados thead th,
.table-totales-terminados tbody td {
  border-color: rgba(0, 0, 0, 0.04) !important;
  border-bottom-width: 1px;
}
.table-totales-terminados tbody tr {
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}
.table-totales-terminados thead th {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* Guiones "—" de celdas vacías muy suaves */
.table-totales-terminados tbody tr.fila-vacia td {
  color: rgba(0, 0, 0, 0.12);
}

.table th,
.table td {
  text-align: center;
}

.table th.text-start,
.table td.text-start {
  text-align: left;
}

.totales-pagination {
  gap: 0.5rem;
}

.totales-pagination-btn {
  min-width: 28px;
  height: 26px;
  padding: 0 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1f2d5a;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.totales-pagination-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #1f2d5a;
}

.totales-pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.totales-pagination-text {
  font-size: 0.85rem;
  color: #1f2d5a;
  min-width: 2rem;
  text-align: center;
}

.totales-chart-wrap {
  position: relative;
  width: 100%;
  min-height: 240px;
  height: 240px;
}

.totales-chart-wrap canvas {
  width: 100% !important;
  height: 100% !important;
  display: block;
}
</style>
